import { call, put, race, select } from 'redux-saga/effects';

import { GeneratorResult, OperationStatus, takeIf } from '@pressreader/appstore';
import { getIssueCalendarInfo } from '@pressreader/content-api';
import { IIssueCalendarInfo } from '@pressreader/content-types';
import { error } from '@pressreader/logger';
import { ExtractReturnType } from '@pressreader/types';

import { loadIssueCalendarInfoBegan, loadIssueCalendarInfoDone, loadIssueCalendarInfoFailed } from '../../actions/issueinfo';
import { selectIssueCalendarInfoByCid } from '../../selectors/issueinfo';

import { isGenerationCurrent } from './generation';

function* getOrWaitForIssueCalendarInfo(cid: string): GeneratorResult<IIssueCalendarInfo | null> {
    const data: ReturnType<typeof selectIssueCalendarInfoByCid> = yield select(selectIssueCalendarInfoByCid, { cid });

    if (data && data.loading !== OperationStatus.None) {
        if (data.loading === OperationStatus.Done) {
            return (yield* isGenerationCurrent(data)) ? data.calendar : null;
        }

        if (data.loading === OperationStatus.Failed) {
            return null;
        }

        yield race({
            done: takeIf(loadIssueCalendarInfoDone, a => a.payload.cid === cid),
            failed: takeIf(loadIssueCalendarInfoFailed, a => a.payload.cid === cid),
        });

        return yield getOrWaitForIssueCalendarInfo(cid);
    }

    return null;
}

function* loadIssueCalendarInfo(cid: string): GeneratorResult<IIssueCalendarInfo | null> {
    const calendarInfo: ExtractReturnType<typeof getOrWaitForIssueCalendarInfo> = yield getOrWaitForIssueCalendarInfo(cid);
    if (calendarInfo) {
        return calendarInfo;
    }

    yield put(loadIssueCalendarInfoBegan({ cid }));

    try {
        const cinfo: ExtractReturnType<typeof getIssueCalendarInfo> = yield call(getIssueCalendarInfo, cid);

        yield put(loadIssueCalendarInfoDone({ cid, calendar: cinfo }));

        return cinfo;
    } catch (e) {
        error(e);
        yield put(loadIssueCalendarInfoFailed({ cid }));
    }

    return null;
}

export { loadIssueCalendarInfo };
